import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
    User,
    Mail,
    Lock,
    CreditCard,
    Sun,
    Moon,
    Download,
    Trash2,
    Crown,
    Loader2,
    Check,
    ExternalLink,
    Bell,
    BellOff,
    FileText,
    Send,
    Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
    const { user, updateUser, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();

    // Profile form
    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [profileLoading, setProfileLoading] = useState(false);

    // Password form
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Delete account
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Push notifications
    const [notificationsEnabled, setNotificationsEnabled] = useState(false);
    const [notificationsSupported, setNotificationsSupported] = useState(false);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [testingNotification, setTestingNotification] = useState(false);

    // Helper function to convert URL-safe base64 to Uint8Array
    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    // Check notification support and status on mount
    useEffect(() => {
        const checkNotifications = async () => {
            // Check if browser supports notifications
            if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
                setNotificationsSupported(true);
                
                // Register service worker if not already registered
                try {
                    const registration = await navigator.serviceWorker.register('/sw.js');
                    console.log('Service Worker registered:', registration);
                } catch (error) {
                    console.log('Service Worker registration failed:', error);
                }
                
                // Check current permission status
                if (Notification.permission === 'granted') {
                    // Check if we have an active subscription on server
                    try {
                        const response = await axios.get(`${API_URL}/api/notifications/settings`);
                        setNotificationsEnabled(response.data?.push_enabled || false);
                    } catch (error) {
                        console.log('Could not fetch notification settings');
                    }
                }
            }
        };
        
        checkNotifications();
    }, []);

    const handleToggleNotifications = async (enabled) => {
        setNotificationsLoading(true);
        
        try {
            if (enabled) {
                // Request permission
                const permission = await Notification.requestPermission();
                
                if (permission === 'granted') {
                    // Register service worker
                    const registration = await navigator.serviceWorker.ready;
                    
                    // Get VAPID public key from server
                    const vapidResponse = await axios.get(`${API_URL}/api/notifications/vapid-public-key`);
                    const vapidPublicKey = vapidResponse.data.publicKey;
                    
                    // Subscribe to push notifications
                    const subscription = await registration.pushManager.subscribe({
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
                    });
                    
                    // Send subscription to server
                    const subscriptionJson = subscription.toJSON();
                    await axios.post(`${API_URL}/api/notifications/subscribe`, {
                        endpoint: subscriptionJson.endpoint,
                        keys: subscriptionJson.keys
                    });
                    
                    setNotificationsEnabled(true);
                    toast.success('Push notifications enabled! You\'ll receive medication reminders.');
                } else if (permission === 'denied') {
                    toast.error('Notification permission denied. Please enable in browser settings.');
                    setNotificationsEnabled(false);
                }
            } else {
                // Unsubscribe from push
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                if (subscription) {
                    await subscription.unsubscribe();
                }
                
                // Remove from server
                await axios.delete(`${API_URL}/api/notifications/unsubscribe`);
                setNotificationsEnabled(false);
                toast.success('Push notifications disabled');
            }
        } catch (error) {
            console.error('Error toggling notifications:', error);
            toast.error('Failed to update notification settings');
        } finally {
            setNotificationsLoading(false);
        }
    };

    const handleTestNotification = async () => {
        setTestingNotification(true);
        try {
            await axios.post(`${API_URL}/api/notifications/test`);
            toast.success('Test notification sent! Check your browser.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send test notification');
        } finally {
            setTestingNotification(false);
        }
    };

    // Weekly Health Reports
    const [weeklyReportsEnabled, setWeeklyReportsEnabled] = useState(false);
    const [weeklyReportEmail, setWeeklyReportEmail] = useState('');
    const [weeklyReportsLoading, setWeeklyReportsLoading] = useState(false);
    const [sendingReport, setSendingReport] = useState(false);
    const [previewHtml, setPreviewHtml] = useState(null);

    // Fetch weekly report settings on mount
    useEffect(() => {
        const fetchWeeklyReportSettings = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/reports/weekly/settings`);
                setWeeklyReportsEnabled(response.data?.enabled || false);
                setWeeklyReportEmail(response.data?.email || user?.email || '');
            } catch (error) {
                console.log('Could not fetch weekly report settings');
            }
        };
        fetchWeeklyReportSettings();
    }, [user?.email]);

    const handleToggleWeeklyReports = async (enabled) => {
        setWeeklyReportsLoading(true);
        try {
            await axios.put(`${API_URL}/api/reports/weekly/settings`, {
                enabled,
                email: weeklyReportEmail || user?.email
            });
            setWeeklyReportsEnabled(enabled);
            toast.success(enabled ? 'Weekly reports enabled' : 'Weekly reports disabled');
        } catch (error) {
            toast.error('Failed to update weekly report settings');
        } finally {
            setWeeklyReportsLoading(false);
        }
    };

    const handleSendReportNow = async () => {
        setSendingReport(true);
        try {
            await axios.post(`${API_URL}/api/reports/weekly/send-now`);
            toast.success('Weekly report sent! Check your email.');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to send report';
            toast.error(message);
        } finally {
            setSendingReport(false);
        }
    };

    const handlePreviewReport = async () => {
        try {
            const response = await axios.post(`${API_URL}/api/reports/weekly/preview`);
            setPreviewHtml(response.data?.html);
        } catch (error) {
            toast.error('Failed to generate preview');
        }
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setProfileLoading(true);

        try {
            const response = await axios.put(`${API_URL}/api/profile`, {
                name: name !== user?.name ? name : undefined,
                email: email !== user?.email ? email : undefined
            });
            updateUser(response.data);
            toast.success('Profile updated successfully');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to update profile';
            toast.error(message);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }

        setPasswordLoading(true);

        try {
            await axios.put(`${API_URL}/api/profile/password`, {
                current_password: currentPassword,
                new_password: newPassword
            });
            toast.success('Password updated successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to update password';
            toast.error(message);
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleDeleteAccount = async () => {
        setDeleteLoading(true);

        try {
            await axios.delete(`${API_URL}/api/account`);
            toast.success('Account deleted successfully');
            logout();
        } catch (error) {
            toast.error('Failed to delete account');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleExportData = () => {
        toast.info('Data export feature coming soon');
    };

    return (
        <div className="space-y-8 max-w-3xl animate-fade-in" data-testid="settings-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account and preferences
                </p>
            </div>

            {/* Account Info */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <User className="w-5 h-5" />
                                Account Information
                            </CardTitle>
                            <CardDescription>Your account details and status</CardDescription>
                        </div>
                        {user?.is_founding_member && (
                            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
                                <Crown className="w-3 h-3" />
                                Founding Member
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-muted/50">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white text-2xl font-bold">
                            {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                            <p className="font-heading font-semibold text-lg">{user?.name}</p>
                            <p className="text-sm text-muted-foreground">{user?.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="capitalize">
                                    {user?.subscription_tier || 'Free'} Plan
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                    Member since {new Date(user?.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Profile Form */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Profile Settings
                    </CardTitle>
                    <CardDescription>Update your personal information</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleUpdateProfile} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Full Name</Label>
                                <Input
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Your name"
                                    data-testid="settings-name"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    data-testid="settings-email"
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={profileLoading || (name === user?.name && email === user?.email)}
                            className="bg-gradient-to-r from-indigo-600 to-violet-600"
                            data-testid="save-profile"
                        >
                            {profileLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Check className="w-4 h-4 mr-2" />
                            )}
                            Save Changes
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Password Form */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Lock className="w-5 h-5" />
                        Change Password
                    </CardTitle>
                    <CardDescription>Update your account password</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleUpdatePassword} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="currentPassword">Current Password</Label>
                            <Input
                                id="currentPassword"
                                type="password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                placeholder="••••••••"
                                data-testid="current-password"
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="newPassword">New Password</Label>
                                <Input
                                    id="newPassword"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="••••••••"
                                    data-testid="new-password"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    data-testid="confirm-password"
                                />
                            </div>
                        </div>
                        <Button
                            type="submit"
                            disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                            variant="outline"
                            data-testid="save-password"
                        >
                            {passwordLoading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Lock className="w-4 h-4 mr-2" />
                            )}
                            Update Password
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Subscription */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Subscription
                    </CardTitle>
                    <CardDescription>Manage your subscription and billing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                        <div>
                            <p className="font-medium capitalize">{user?.subscription_tier || 'Free'} Plan</p>
                            <p className="text-sm text-muted-foreground">
                                {user?.subscription_tier === 'free' 
                                    ? 'Upgrade to unlock all features'
                                    : 'Your subscription is active'}
                            </p>
                        </div>
                        <Button variant="outline" className="gap-2" disabled>
                            <ExternalLink className="w-4 h-4" />
                            {user?.subscription_tier === 'free' ? 'Upgrade' : 'Manage'}
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Stripe integration will be available once Price IDs are configured.
                    </p>
                </CardContent>
            </Card>

            {/* Preferences */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading">Preferences</CardTitle>
                    <CardDescription>Customize your app experience</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Dark Mode Toggle */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {theme === 'dark' ? (
                                <Moon className="w-5 h-5 text-muted-foreground" />
                            ) : (
                                <Sun className="w-5 h-5 text-muted-foreground" />
                            )}
                            <div>
                                <p className="font-medium">Dark Mode</p>
                                <p className="text-sm text-muted-foreground">
                                    {theme === 'dark' ? 'Currently using dark theme' : 'Currently using light theme'}
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={theme === 'dark'}
                            onCheckedChange={toggleTheme}
                            data-testid="dark-mode-toggle"
                        />
                    </div>

                    <Separator />

                    {/* Push Notifications Toggle */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {notificationsEnabled ? (
                                <Bell className="w-5 h-5 text-muted-foreground" />
                            ) : (
                                <BellOff className="w-5 h-5 text-muted-foreground" />
                            )}
                            <div>
                                <p className="font-medium">Push Notifications</p>
                                <p className="text-sm text-muted-foreground">
                                    {!notificationsSupported 
                                        ? 'Not supported in this browser'
                                        : notificationsEnabled 
                                            ? 'Receive reminders for medications and appointments'
                                            : 'Enable to receive health reminders'}
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={notificationsEnabled}
                            onCheckedChange={handleToggleNotifications}
                            disabled={!notificationsSupported || notificationsLoading}
                            data-testid="notifications-toggle"
                        />
                    </div>
                    {notificationsEnabled && (
                        <div className="ml-8 space-y-3">
                            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 text-sm text-emerald-700 dark:text-emerald-400">
                                <p>You'll receive reminders for:</p>
                                <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
                                    <li>Daily medication and supplement reminders</li>
                                    <li>Upcoming appointment notifications</li>
                                    <li>Daily check-in prompts</li>
                                </ul>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleTestNotification}
                                disabled={testingNotification}
                                className="gap-2"
                                data-testid="test-notification-btn"
                            >
                                {testingNotification ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Bell className="w-4 h-4" />
                                )}
                                Send Test Notification
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Data Export */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Download className="w-5 h-5" />
                        Export Data
                    </CardTitle>
                    <CardDescription>Download all your data</CardDescription>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                        Export all your tracking data, check-ins, and flare logs as a JSON file.
                    </p>
                    <Button variant="outline" onClick={handleExportData} className="gap-2">
                        <Download className="w-4 h-4" />
                        Export All Data
                    </Button>
                </CardContent>
            </Card>

            {/* Weekly Health Reports */}
            <Card className="border-purple-200 dark:border-purple-800 opacity-75">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <FileText className="w-5 h-5 text-purple-500" />
                        Weekly Health Reports
                        <Badge className="ml-2 text-xs bg-amber-500 text-white">Coming Soon</Badge>
                    </CardTitle>
                    <CardDescription>Receive weekly email summaries of your health insights</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5 text-muted-foreground" />
                            <div>
                                <p className="font-medium">Email Reports</p>
                                <p className="text-sm text-muted-foreground">
                                    Weekly summaries with insights and patterns - launching soon!
                                </p>
                            </div>
                        </div>
                        <Switch
                            checked={false}
                            disabled={true}
                            data-testid="weekly-reports-toggle"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Danger Zone */}
            <Card className="border-destructive/50">
                <CardHeader>
                    <CardTitle className="font-heading text-destructive flex items-center gap-2">
                        <Trash2 className="w-5 h-5" />
                        Danger Zone
                    </CardTitle>
                    <CardDescription>Irreversible actions</CardDescription>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                        Once you delete your account, there is no going back. This will permanently delete all your data.
                    </p>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="destructive" className="gap-2" data-testid="delete-account">
                                <Trash2 className="w-4 h-4" />
                                Delete Account
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This action cannot be undone. This will permanently delete your account
                                    and remove all your data from our servers.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={handleDeleteAccount}
                                    disabled={deleteLoading}
                                    className="bg-destructive hover:bg-destructive/90"
                                    data-testid="confirm-delete"
                                >
                                    {deleteLoading ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : null}
                                    Delete Account
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </CardContent>
            </Card>
        </div>
    );
}
